import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { employeeAPI, modulePermissionAPI, customerAssignmentAPI, authAPI } from "../../services/api";
import { useLanguage } from "../../context/LanguageContext";
import settingsLanguage from "../../language/settingsLanguage";
import { useAlert } from "../../Hooks/useAlert";
import Alert from "../common/Alert";
import ConfirmDialog from "../common/ConfirmDialog";
import Spinner from "../common/Spinner";
import BackButton from "./SettingsSections/BackButton";

const MODULE_GROUPS = [
  { label: "Home", code: "HOME", icon: "fa-home", color: "blue" },
  { label: "Dashboard", code: "DASHBOARD", icon: "fa-chart-line", color: "emerald" },
  { label: "Report", code: "REPORT.REPORT", icon: "fa-file-alt", color: "violet", group: "Report & Billing" },
  { label: "Bill", code: "REPORT.BILL", icon: "fa-file-invoice-dollar", color: "violet", group: "Report & Billing" },
  { label: "Customer Management", code: "SETTINGS.CUSTOMER", icon: "fa-users", color: "orange", group: "Manage" },
  { label: "Master Management", code: "SETTINGS.MASTER", icon: "fa-database", color: "orange", group: "Manage" },
  { label: "Sales & Expenses", code: "SETTINGS.SALES", icon: "fa-chart-pie", color: "orange", group: "Manage" },
];

function EmployeeManagementPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const text = settingsLanguage[language];
  const { showAlert, showConfirm, alertConfig, confirmConfig } = useAlert();
  const currentUser = authAPI.getCurrentUser();
  const userId = currentUser?.userId;

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [formUsername, setFormUsername] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");

  const [permModal, setPermModal] = useState(null);
  const [permLoading, setPermLoading] = useState(false);
  const [permValues, setPermValues] = useState({});

  const [custModal, setCustModal] = useState(null);
  const [custLoading, setCustLoading] = useState(false);
  const [assignedCustomers, setAssignedCustomers] = useState([]);
  const [unassignedCustomers, setUnassignedCustomers] = useState([]);
  const [custSearch, setCustSearch] = useState("");
  const [assignedSearch, setAssignedSearch] = useState("");
  const [custTab, setCustTab] = useState("available");

  const loadEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const response = await employeeAPI.getEmployeesByUser(userId);
      if (response?.success) {
        setEmployees(response.data || []);
      }
    } catch (err) {
      console.error("Error loading employees:", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  const handleAddEmployee = () => {
    setEditEmployee(null);
    setFormUsername("");
    setFormPassword("");
    setShowPassword(false);
    setFormError("");
    setShowForm(true);
  };

  const handleEditEmployee = (emp) => {
    setEditEmployee(emp);
    setFormUsername(emp.username);
    setFormPassword(emp.plainPassword || "");
    setShowPassword(false);
    setFormError("");
    setShowForm(true);
  };

  const handleSaveEmployee = async () => {
    if (!formUsername.trim()) { setFormError("Username is required"); return; }
    if (!editEmployee && !formPassword.trim()) { setFormError("Password is required"); return; }
    if (editEmployee && formPassword.trim() && formPassword.trim().length < 4) { setFormError("Password must be at least 4 characters"); return; }

    try {
      setLoading(true);
      if (editEmployee) {
        const resp = await employeeAPI.updateEmployee(editEmployee.employeeId, userId, formUsername.trim(), formPassword.trim() || undefined);
        if (resp?.success) {
          await showAlert({ type: "success", title: "Success", message: "Employee updated" });
        } else {
          await showAlert({ type: "error", title: "Error", message: resp?.message || "Failed to update" });
        }
      } else {
        const resp = await employeeAPI.createEmployee(userId, formUsername.trim(), formPassword);
        if (resp?.success) {
          await showAlert({ type: "success", title: "Success", message: "Employee created" });
        } else {
          await showAlert({ type: "error", title: "Error", message: resp?.message || "Failed to create" });
        }
      }
      setShowForm(false);
      await loadEmployees();
    } catch (err) {
      await showAlert({ type: "error", title: "Error", message: err.response?.data?.message || "Operation failed" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmployee = async (emp) => {
    const confirmed = await showConfirm({
      type: "warning", title: text.delete + " " + text.employeeManagement,
      message: `Are you sure you want to delete "${emp.username}"?`,
      confirmText: text.delete, cancelText: text.cancel
    });
    if (!confirmed) return;
    try {
      setLoading(true);
      const resp = await employeeAPI.deleteEmployee(emp.employeeId);
      if (resp?.success) {
        await showAlert({ type: "success", title: "Success", message: "Employee deleted" });
        await loadEmployees();
      }
    } catch (err) {
      await showAlert({ type: "error", title: "Error", message: err.response?.data?.message || "Delete failed" });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (emp) => {
    const action = emp.isActive ? "deactivate" : "activate";
    const confirmed = await showConfirm({
      type: "warning", title: `${action === "activate" ? text.activate : text.deactivate} ${text.employeeManagement}`,
      message: `Are you sure you want to ${action} "${emp.username}"?`,
      confirmText: action === "activate" ? text.activate : text.deactivate, cancelText: text.cancel
    });
    if (!confirmed) return;
    try {
      setLoading(true);
      const resp = await employeeAPI.toggleEmployeeStatus(emp.employeeId, !emp.isActive);
      if (resp?.success) {
        await showAlert({ type: "success", title: "Success", message: `Employee ${action}d` });
        await loadEmployees();
      }
    } catch (err) {
      await showAlert({ type: "error", title: "Error", message: err.response?.data?.message || "Toggle failed" });
    } finally {
      setLoading(false);
    }
  };

  const openPermModal = async (emp) => {
    setPermModal(emp);
    setPermLoading(true);
    try {
      const resp = await modulePermissionAPI.getEmployeePermissions(emp.employeeId);
      const perms = resp?.data?.permissions || [];
      const values = {};
      MODULE_GROUPS.forEach(m => { values[m.code] = false; });
      perms.forEach(p => { if (values.hasOwnProperty(p.moduleCode)) values[p.moduleCode] = p.isEnabled; });
      setPermValues(values);
    } catch (err) {
      console.error(err);
    } finally {
      setPermLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    const permArray = Object.entries(permValues).map(([moduleCode, isEnabled]) => ({ moduleCode, isEnabled }));
    try {
      setPermLoading(true);
      const resp = await modulePermissionAPI.setEmployeePermissions(permModal.employeeId, permArray);
      if (resp?.success) {
        await showAlert({ type: "success", title: "Success", message: "Permissions saved" });
        setPermModal(null);
        await loadEmployees();
      }
    } catch (err) {
      await showAlert({ type: "error", title: "Error", message: err.response?.data?.message || "Failed" });
    } finally {
      setPermLoading(false);
    }
  };

  const openCustModal = async (emp) => {
    setCustModal(emp);
    setCustLoading(true);
    setCustSearch("");
    setAssignedSearch("");
    try {
      const [assignedResp, unassignedResp] = await Promise.all([
        customerAssignmentAPI.getEmployeeCustomers(emp.employeeId),
        customerAssignmentAPI.getUnassignedCustomers(userId)
      ]);
      setAssignedCustomers(assignedResp?.data || []);
      setUnassignedCustomers(unassignedResp?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setCustLoading(false);
    }
  };

  const handleAssignCustomer = async (customerId) => {
    try {
      const resp = await customerAssignmentAPI.assignCustomer(custModal.employeeId, customerId);
      if (resp?.success) {
        const [assignedResp, unassignedResp] = await Promise.all([
          customerAssignmentAPI.getEmployeeCustomers(custModal.employeeId),
          customerAssignmentAPI.getUnassignedCustomers(userId)
        ]);
        setAssignedCustomers(assignedResp?.data || []);
        setUnassignedCustomers(unassignedResp?.data || []);
      } else {
        await showAlert({ type: "error", title: "Error", message: resp?.message || "Assignment failed" });
      }
    } catch (err) {
      await showAlert({ type: "error", title: "Error", message: err.response?.data?.message || "Failed" });
    }
  };

  const handleRemoveCustomer = async (customerId) => {
    try {
      const resp = await customerAssignmentAPI.removeCustomer(customerId);
      if (resp?.success) {
        const [assignedResp, unassignedResp] = await Promise.all([
          customerAssignmentAPI.getEmployeeCustomers(custModal.employeeId),
          customerAssignmentAPI.getUnassignedCustomers(userId)
        ]);
        setAssignedCustomers(assignedResp?.data || []);
        setUnassignedCustomers(unassignedResp?.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const filteredEmployees = employees.filter(e =>
    e.username?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredUnassigned = unassignedCustomers.filter(c =>
    c.customerName?.toLowerCase().includes(custSearch.toLowerCase())
  );

  return (
    <div className="settings-page">
      <header className="settings-page-header">
        <BackButton to="/settings" section="employee" />
        <h1 className="settings-page-title">{text.employeeManagement || "Employee Management"}</h1>
      </header>

      <main className="settings-page-content">
        <div className="space-y-3">

        <button onClick={handleAddEmployee}
          className="w-full px-3 py-2 bg-primary hover:bg-primary-dark text-white text-sm rounded-lg transition flex items-center justify-center gap-1.5 font-semibold">
          <i className="fas fa-plus" /> <span>{text.addEmployee}</span>
        </button>

        <div className="relative">
          <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder={text.searchEmployees} autoComplete="off"
            className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
        </div>

        {showForm && (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-4">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-white mb-3">
              {editEmployee ? text.editEmployee : text.addNewEmployee}
            </h2>
            <div className="space-y-3 max-w-md">
              <div className="settings-form-group">
                <label className="settings-form-label">{text.username}</label>
                <input type="text" value={formUsername} onChange={e => setFormUsername(e.target.value)}
                  className="settings-form-input" placeholder={text.enterUsername} autoComplete="off" />
              </div>
              <div className="settings-form-group">
                  <label className="settings-form-label">{text.password}</label>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} value={formPassword} onChange={e => setFormPassword(e.target.value)}
                      className="settings-form-input pr-10" placeholder={text.enterPassword} />
                    <button type="button" onClick={() => setShowPassword(p => !p)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm p-1">
                      <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                    </button>
                  </div>
                </div>
              {formError && <p className="settings-form-error">{formError}</p>}
              <div className="flex gap-2">
                <button onClick={handleSaveEmployee} disabled={loading}
                  className="settings-form-btn">{editEmployee ? text.update : text.create}</button>
                <button onClick={() => setShowForm(false)}
                  className="settings-form-btn settings-form-btn-secondary">{text.cancel}</button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden flex flex-col max-h-[calc(100vh-280px)]">
          {loading ? <div className="p-8"><Spinner /></div> : (
            <div className="overflow-x-auto overflow-y-auto flex-1">
              <table className="w-full">
                <thead className="bg-slate-100 dark:bg-slate-700 sticky top-0 z-10">
                  <tr>
                    <th className="px-2 md:px-3 py-2 text-left text-[10px] md:text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">{text.id}</th>
                    <th className="px-2 md:px-3 py-2 text-left text-[10px] md:text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">{text.username}</th>
                    <th className="px-2 md:px-3 py-2 text-center text-[10px] md:text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">{text.status}</th>
                    <th className="px-2 md:px-3 py-2 text-center text-xs md:text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase hidden md:table-cell">{text.customers}</th>

                    <th className="px-2 md:px-3 py-2 text-center text-xs md:text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase hidden md:table-cell">{text.modules}</th>

                    <th className="px-2 md:px-3 py-2 text-center text-xs md:text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase hidden lg:table-cell">{text.created}</th>
                    <th className="px-2 md:px-3 py-2 text-center text-[10px] md:text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">{text.actions}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {filteredEmployees.length > 0 ? filteredEmployees.map(emp => (
                    <tr key={emp.employeeId} className="hover:bg-slate-50 dark:hover:bg-slate-700 transition">
                      <td className="px-2 md:px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900 dark:text-white">{emp.employeeId}</td>
                      <td className="px-2 md:px-3 py-2 whitespace-nowrap text-xs text-gray-900 dark:text-white">{emp.username}</td>
                      <td className="px-2 md:px-3 py-2 whitespace-nowrap text-center">
                        <span className={`px-1.5 py-0.5 inline-flex text-[10px] font-semibold rounded-full ${emp.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                          {emp.isActive ? text.active : text.inactive}
                        </span>
                      </td>
                      <td className="px-2 md:px-3 py-2 whitespace-nowrap text-center text-sm text-gray-600 dark:text-gray-400 hidden md:table-cell">{emp.assignedCustomerCount}</td>
                      <td className="px-2 md:px-3 py-2 whitespace-nowrap text-center text-sm text-gray-600 dark:text-gray-400 hidden md:table-cell">{emp.moduleCount}</td>
                      <td className="px-2 md:px-3 py-2 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400 text-center hidden lg:table-cell">{new Date(emp.createdDate).toLocaleDateString()}</td>
                      <td className="px-2 md:px-3 py-2 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => openPermModal(emp)} className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded" title={text.assignPermissions}><i className="fas fa-shield-alt text-xs" /></button>
                          <button onClick={() => openCustModal(emp)} className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded" title={text.assignCustomers}><i className="fas fa-users text-xs" /></button>
                          <button onClick={() => handleEditEmployee(emp)} className="p-1.5 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded" title={text.edit}><i className="fas fa-pen text-xs" /></button>
                          <button onClick={() => handleToggleStatus(emp)} className={`p-1.5 rounded ${emp.isActive ? "text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20" : "text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"}`} title={emp.isActive ? text.deactivate : text.activate}><i className={`fas ${emp.isActive ? "fa-ban" : "fa-check"} text-xs`} /></button>
                          <button onClick={() => handleDeleteEmployee(emp)} className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded" title={text.delete}><i className="fas fa-trash text-xs" /></button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="7" className="px-3 py-12 text-center text-gray-500 dark:text-gray-400">
                      <i className="fas fa-user-tie text-4xl mb-3 block opacity-50" />
                      <p className="text-sm">{text.noEmployeesFound}</p>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {alertConfig && <Alert {...alertConfig} />}
      {confirmConfig && <ConfirmDialog {...confirmConfig} />}

      {permModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="fas fa-shield-alt text-white text-sm" />
                </div>
                <div>
                  <h3 className="text-white text-sm font-bold">{text.modulePermissions}</h3>
                  <p className="text-white/70 text-xs">{permModal.username}</p>
                </div>
              </div>
              <button onClick={() => setPermModal(null)} className="text-white/80 hover:text-white text-lg"><i className="fas fa-times" /></button>
            </div>
            <div className="p-5 max-h-96 overflow-y-auto space-y-4">
              {permLoading ? <div className="flex justify-center py-8"><Spinner /></div> : (() => {
                const groups = {};
                MODULE_GROUPS.forEach(m => {
                  const g = m.group || "General";
                  if (!groups[g]) groups[g] = [];
                  groups[g].push(m);
                });
                const groupLabels = { "General": "", "Report & Billing": text.groupReportBilling, "Manage": text.groupManage };
                const groupIcons = { "General": "fa-star", "Report & Billing": "fa-file-alt", "Manage": "fa-cog" };
                const groupColors = { "General": "blue", "Report & Billing": "violet", "Manage": "orange" };
                const colorMap = { blue: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800", emerald: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800", violet: "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800", orange: "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800" };
                const checkboxColor = { blue: "accent-blue-600", emerald: "accent-emerald-600", violet: "accent-violet-600", orange: "accent-orange-600" };
                return Object.entries(groups).map(([groupName, items]) => (
                  <div key={groupName}>
                    <div className="flex items-center gap-2 mb-2 pb-1 border-b border-gray-200 dark:border-slate-700">
                      <i className={`fas ${groupIcons[groupName] || "fa-circle"} text-${groupColors[groupName] || "gray"}-500 text-xs`} />
                      <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{groupLabels[groupName] || groupName}</span>
                    </div>
                    <div className="grid grid-cols-1 gap-1.5">
                      {items.map(m => (
                        <label key={m.code} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${permValues[m.code] ? (colorMap[m.color] || colorMap.blue) + " shadow-sm" : "bg-gray-50 dark:bg-slate-700/50 border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700"}`}>
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs ${permValues[m.code] ? "bg-white/80 text-gray-700" : "bg-gray-200 dark:bg-slate-600 text-gray-500 dark:text-gray-300"}`}>
                            <i className={`fas ${m.icon}`} />
                          </div>
                          <span className={`flex-1 text-sm font-medium ${permValues[m.code] ? "text-gray-800 dark:text-gray-100" : "text-gray-700 dark:text-gray-300"}`}>{m.label}</span>
                          <input type="checkbox" checked={!!permValues[m.code]} onChange={e => setPermValues(p => ({ ...p, [m.code]: e.target.checked }))}
                            className={`w-4 h-4 rounded ${checkboxColor[m.color] || "accent-blue-600"} cursor-pointer`} />
                        </label>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/80 flex gap-2 justify-end">
              <button onClick={() => setPermModal(null)} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 rounded-lg transition">{text.cancel}</button>
              <button onClick={handleSavePermissions} disabled={permLoading} className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-lg transition disabled:opacity-50 flex items-center gap-2">
                {permLoading ? <><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> {text.saving}</> : <><i className="fas fa-check" /> {text.savePermissions}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {custModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 md:p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-teal-600 px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="fas fa-users text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold">{text.customerAssignment}</h3>
                  <p className="text-white/70 text-xs">{custModal.username}</p>
                </div>
              </div>
              <button onClick={() => setCustModal(null)} className="text-white/80 hover:text-white text-lg"><i className="fas fa-times" /></button>
            </div>

            {custLoading ? (
              <div className="flex-1 flex items-center justify-center p-12"><Spinner /></div>
            ) : (
              <>
                {/* Mobile Tab Switcher — visible only on small screens */}
                <div className="flex md:hidden border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                  <button onClick={() => setCustTab("available")}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-3 px-2 text-xs font-bold uppercase tracking-wider transition-all ${
                      custTab === "available"
                        ? "text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 border-b-2 border-green-600"
                        : "text-gray-500 dark:text-gray-400"
                    }`}>
                    <i className="fas fa-user-plus text-sm" />
                    <span>{text.available}</span>
                    <span className="text-[10px] bg-gray-200 dark:bg-slate-600 px-1.5 py-0.5 rounded-full">{unassignedCustomers.length}</span>
                  </button>
                  <button onClick={() => setCustTab("assigned")}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-3 px-2 text-xs font-bold uppercase tracking-wider transition-all ${
                      custTab === "assigned"
                        ? "text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 border-b-2 border-green-600"
                        : "text-gray-500 dark:text-gray-400"
                    }`}>
                    <i className="fas fa-user-check text-sm" />
                    <span>{text.assigned}</span>
                    <span className="text-[10px] bg-gray-200 dark:bg-slate-600 px-1.5 py-0.5 rounded-full">{assignedCustomers.length}</span>
                  </button>
                </div>

                <div className="flex-1 flex flex-col md:flex-row min-h-0">
                  {/* Available Customers Panel */}
                  <div className={`flex-1 flex flex-col min-h-0 ${custTab === "assigned" ? "hidden md:flex" : ""} border-r border-gray-200 dark:border-slate-700`}>
                    <div className="hidden md:flex px-4 py-3 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400"><i className="fas fa-user-plus text-sm" />{text.available}</span>
                        <span className="text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">{unassignedCustomers.length}</span>
                      </div>
                      <div className="relative">
                        <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <input type="text" value={custSearch} onChange={e => setCustSearch(e.target.value)}
                          placeholder={text.searchCustomers} autoComplete="off"
                          className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/20" />
                      </div>
                    </div>
                    {/* Mobile search inside panel */}
                    <div className="md:hidden px-3 py-2 border-b border-gray-200 dark:border-slate-700">
                      <div className="relative">
                        <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <input type="text" value={custSearch} onChange={e => setCustSearch(e.target.value)}
                          placeholder={text.searchCustomers} autoComplete="off"
                          className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/20" />
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
                      {filteredUnassigned.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
                          <i className="fas fa-check-circle text-4xl mb-2" />
                          <p className="text-xs">{text.noUnassignedCustomers}</p>
                        </div>
                      ) : filteredUnassigned.map(c => (
                        <button key={c.customerId} onClick={() => handleAssignCustomer(c.customerId)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 md:py-2 rounded-xl md:rounded-lg text-left transition-all hover:bg-blue-50 dark:hover:bg-blue-900/20 group border border-transparent hover:border-blue-200 dark:hover:border-blue-800 active:scale-[0.98]">
                          <div className="w-8 h-8 md:w-7 md:h-7 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-xs text-gray-500 dark:text-gray-300 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/40">
                            <i className="fas fa-user" />
                          </div>
                          <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-700 dark:group-hover:text-blue-300 truncate">{c.customerName}</span>
                          <span className="flex items-center gap-1 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 px-3 py-1.5 md:px-2.5 md:py-1 rounded-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shadow-sm">
                            <i className="fas fa-plus-circle text-[10px]" /> <span className="hidden md:inline">{text.assign}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Assigned Customers Panel */}
                  <div className={`flex-1 flex flex-col min-h-0 md:max-w-[45%] ${custTab === "available" ? "hidden md:flex" : ""}`}>
                    <div className="hidden md:flex px-4 py-3 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400"><i className="fas fa-user-check text-sm" />{text.assigned}</span>
                        <span className="text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full">{assignedCustomers.length}</span>
                      </div>
                      <div className="relative">
                        <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <input type="text" value={assignedSearch} onChange={e => setAssignedSearch(e.target.value)}
                          placeholder={text.searchCustomers} autoComplete="off"
                          className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/20" />
                      </div>
                    </div>
                    {/* Mobile search inside panel */}
                    <div className="md:hidden px-3 py-2 border-b border-gray-200 dark:border-slate-700">
                      <div className="relative">
                        <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <input type="text" value={assignedSearch} onChange={e => setAssignedSearch(e.target.value)}
                          placeholder={text.searchCustomers} autoComplete="off"
                          className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/20" />
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
                      {assignedCustomers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
                          <i className="fas fa-inbox text-4xl mb-2" />
                          <p className="text-xs">{text.noCustomersAssigned}</p>
                        </div>
                      ) : (() => {
                        const searched = assignedCustomers.filter(c =>
                          c.customerName?.toLowerCase().includes(assignedSearch.toLowerCase())
                        );
                        return searched.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
                            <i className="fas fa-search text-4xl mb-2" />
                            <p className="text-xs">{text.noMatchingAssigned}</p>
                          </div>
                        ) : searched.map(c => (
                          <button key={c.customerId} onClick={() => handleRemoveCustomer(c.customerId)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 md:py-2 rounded-xl md:rounded-lg text-left transition-all hover:bg-red-50 dark:hover:bg-red-900/20 group border border-transparent hover:border-red-200 dark:hover:border-red-800 active:scale-[0.98]">
                            <div className="w-8 h-8 md:w-7 md:h-7 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-xs text-emerald-600 dark:text-emerald-400 group-hover:bg-red-100 dark:group-hover:bg-red-900/40">
                              <i className="fas fa-check" />
                            </div>
                            <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-red-600 dark:group-hover:text-red-400 truncate">{c.customerName}</span>
                            <span className="flex items-center gap-1 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 px-3 py-1.5 md:px-2.5 md:py-1 rounded-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shadow-sm">
                              <i className="fas fa-minus-circle text-[10px]" /> <span className="hidden md:inline">{text.remove}</span>
                            </span>
                          </button>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      </main>
    </div>
  );
}

export default EmployeeManagementPage;
